import React from "react";
import { ArrowDown, ArrowLeftRight, ArrowUp, GripVertical } from "lucide-react";
import {
  MAX_PIVOT_COLUMNS,
  MAX_PIVOT_ROWS,
  movePivotPair,
} from "./customReportVisualizationConfig.js";

const Shelf = ({ name, label, limit, pairs, getPairLabel, onMove, onDropPair }) => (
  <div
    className="rounded-md border border-gray-200 bg-gray-50/80 p-2"
    onDragOver={(event) => event.preventDefault()}
    onDrop={(event) => {
      event.preventDefault();
      try {
        const payload = JSON.parse(event.dataTransfer.getData("application/json"));
        onDropPair(payload.shelf, payload.index, name, pairs.length);
      } catch (error) {
        // Ignore drag data from outside this editor.
      }
    }}
  >
    <div className="mb-2 flex items-center justify-between">
      <span className="text-xs font-medium text-gray-700">{label}</span>
      <span className="rounded bg-white px-2 py-0.5 text-[11px] text-gray-500">
        {pairs.length}/{limit}
      </span>
    </div>
    <div className="space-y-1.5">
      {pairs.length === 0 ? (
        <div className="rounded border border-dashed border-gray-300 bg-white px-3 py-4 text-center text-xs text-gray-400">
          Drag a dimension here
        </div>
      ) : (
        pairs.map((pair, index) => (
          <div
            key={`${name}-${pair.module}-${pair.value}-${index}`}
            draggable
            onDragStart={(event) => {
              event.dataTransfer.effectAllowed = "move";
              event.dataTransfer.setData("application/json", JSON.stringify({ shelf: name, index }));
            }}
            onDragOver={(event) => event.preventDefault()}
            onDrop={(event) => {
              event.preventDefault();
              event.stopPropagation();
              try {
                const payload = JSON.parse(event.dataTransfer.getData("application/json"));
                onDropPair(payload.shelf, payload.index, name, index);
              } catch (error) {
                // Ignore drag data from outside this editor.
              }
            }}
            className="flex items-center gap-2 rounded border border-gray-200 bg-white px-2 py-1.5 text-xs text-gray-800 shadow-sm"
          >
            <GripVertical className="h-4 w-4 shrink-0 cursor-grab text-gray-400" aria-hidden />
            <span className="min-w-0 flex-1 truncate" title={getPairLabel(pair)}>
              {getPairLabel(pair)}
            </span>
            <div className="flex shrink-0 items-center gap-0.5">
              <button
                type="button"
                aria-label={`Move ${getPairLabel(pair)} up`}
                disabled={index === 0}
                onClick={() => onMove(name, index, name, index - 1)}
                className="rounded p-1 text-gray-500 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-30"
              >
                <ArrowUp className="h-3.5 w-3.5" />
              </button>
              <button
                type="button"
                aria-label={`Move ${getPairLabel(pair)} down`}
                disabled={index === pairs.length - 1}
                onClick={() => onMove(name, index, name, index + 1)}
                className="rounded p-1 text-gray-500 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-30"
              >
                <ArrowDown className="h-3.5 w-3.5" />
              </button>
              <button
                type="button"
                aria-label={`Move ${getPairLabel(pair)} to ${name === "rows" ? "columns" : "rows"}`}
                onClick={() => onMove(name, index, name === "rows" ? "columns" : "rows", 999)}
                className="rounded p-1 text-indigo-600 hover:bg-indigo-50"
              >
                <ArrowLeftRight className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
        ))
      )}
    </div>
  </div>
);

const PivotShelvesEditor = ({
  pivotConfig,
  fieldOptions = [],
  errors = [],
  onChange,
  movePair = movePivotPair,
  rowLimit = MAX_PIVOT_ROWS,
  columnLimit = MAX_PIVOT_COLUMNS,
  title = "Pivot shelves",
  description = "Use up to three row dimensions and two column dimensions. Every selected parameter must be on one shelf.",
}) => {
  const config = {
    rows: Array.isArray(pivotConfig?.rows) ? pivotConfig.rows : [],
    columns: Array.isArray(pivotConfig?.columns) ? pivotConfig.columns : [],
  };
  const getPairLabel = (pair) =>
    fieldOptions.find(
      (option) => option.module === pair.module && String(option.paramValue) === String(pair.value)
    )?.label || `${pair.module}.${pair.value}`;
  const move = (fromShelf, fromIndex, toShelf, toIndex) =>
    onChange(movePair(config, fromShelf, fromIndex, toShelf, toIndex));

  return (
    <div className="mt-3">
      <div className="mb-2 flex items-center justify-between gap-3">
        <label className="block text-xs text-gray-500">{title}</label>
        {pivotConfig?.isLegacy ? (
          <span className="text-[11px] text-amber-700">Legacy axis order is preserved until changed.</span>
        ) : null}
      </div>
      <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
        <Shelf
          name="rows"
          label="Rows"
          limit={rowLimit}
          pairs={config.rows}
          getPairLabel={getPairLabel}
          onMove={move}
          onDropPair={move}
        />
        <Shelf
          name="columns"
          label="Columns"
          limit={columnLimit}
          pairs={config.columns}
          getPairLabel={getPairLabel}
          onMove={move}
          onDropPair={move}
        />
      </div>
      <p className="mt-2 text-[11px] text-gray-500">
        {description}
      </p>
      {errors.map((error, index) => (
        <p key={`${error.field}-${index}`} role="alert" className="mt-1 text-xs text-red-600">
          {error.message}
        </p>
      ))}
    </div>
  );
};

export default PivotShelvesEditor;
